import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { IoMdClose } from "react-icons/io";
import {
  MdCancel,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from "react-icons/md";
import { BudgetService } from "../../../../services/budget.service";
import { BudgetInfoComparison } from "../../../../types/budget/comparison-types";
import {
  buildComparisonCategoryTotalRows,
  getComparisonServiceValorTotalDisplay,
  getComparisonServiceValorUnitarioDisplay,
  getComparisonSummaryTaxFlags,
  getExportParityCategorySummaryTotalBrl,
  getExportParityCategorySummaryVu,
  resolveComparisonServiceValues as resolveServiceValues,
  type ComparisonCategoryTotalRowDef,
} from "../../../../utils/budgetComparisonTaxDisplay";
import { BudgetItemTypeEnum } from "../../../../types/budget/budget";
import { ServiceOptions } from "../../../../utils/serviceTypesFromBudget";
import {
  comparisonBudgetDisplayName,
  comparisonBudgetVersionHistory,
  findComparisonBudget,
  type ComparisonModalBudgetList,
} from "../comparison-modal-list";
import { ObsCell } from "../ObsCell";

// ─── palette & layout constants (mirrors BudgetComparisonModal) ───────────────
const C = {
  sectionBg: "#3b668b",
  grandTotalBg: "#274966",
  categoryBg: "#f8f8f8",
  separatorBg: "#e0e0e0",
  border: "#e0e0e0",
  cellText: "#222",
  titleText: "#4b465c",
  descText: "#6f6b7d",
  primary: "#1361a4",
  linkBlue: "#1976d2",
  chipBg: "#e5e8eb",
  controlBorder: "#c4c4c4",
  white: "#ffffff",
  savingsNeg: "#D32F2F",
  savingsPos: "#2e7d32",
};

const FONT = "'Public Sans', sans-serif";
const bdr = `1px solid ${C.border}`;
/** Coluna de rótulo: títulos longos podem quebrar linha; largura moderada. */
const LABEL_W = 328;
const HEADER_H = 48;
// Meio-termo: cabe moeda pt-BR grande sem faixas vazias excessivas.
// Primeira coluna (Qtd) um pouco mais larga para não “invadir” o bloco do orçamento ao lado.
const OBS_W = 220;
const DESCRICAO_W = 200;
const COL_WIDTHS = [DESCRICAO_W, 76, 88, 158, 180, 168, OBS_W];
const COL_LABELS = [
  "Descrição",
  "Qtde",
  "Diárias",
  "Valor Unitário",
  "Valor Total",
  "Savings",
  "Obs. Internas",
];
/** Orçamento base (v0): Descrição + Qtd/Diárias/VU/Valor total + Obs. Internas (sem slot de Savings). */
const BASE_COL_WIDTHS = [...COL_WIDTHS.slice(0, 5), OBS_W];
/** Índices fixos das subcolunas (Valor Total e Savings) e posição de Descrição/Obs no array de valores. */
const IDX_DESCRICAO_VAL = 0;
const IDX_VALOR_TOTAL = 4;
const IDX_SAVINGS = 5;
const IDX_OBS_VAL = 6;
const SAVING_TOTAL_W = 168;

/** Divisória vertical entre cada bloco Orçamento / Negociação / Saving Total */
const GROUP_DIVIDER_R = "3px solid #7A909F";
/** Linhas internas nas linhas de fundo azul (alinha com a divisória principal; evita “corte” visual) */
const SECTION_INNER_R = "1px solid rgba(255,255,255,0.32)";

function colWidthsForNegotiationBudget(bd: BudgetInfoComparison): number[] {
  return bd.version === 0 ? BASE_COL_WIDTHS : COL_WIDTHS;
}

function colCountForNegotiationBudget(bd: BudgetInfoComparison): number {
  return colWidthsForNegotiationBudget(bd).length;
}

// Savings column: difference vs the column immediately to the left (same as Excel export:
// currentTotal - previousTotal). API often omits savings on negotiation comparison payloads.
// savingsRaw > 0 → more expensive than previous column → red.
// savingsRaw <= 0 → same or cheaper than previous → green.
function savingsStyle(savingsRaw: number): { color: string; bgcolor: string } {
  return savingsRaw > 0
    ? { color: C.savingsNeg, bgcolor: "rgba(211,47,47,0.06)" }
    : { color: C.savingsPos, bgcolor: "rgba(46,125,50,0.06)" };
}

// Valor Total column: compare current version against base (version 0).
// If current > base → red (price went up). If current < base → green (price went down).
function totalStyle(
  current: number,
  base: number
): { color: string; bgcolor: string } {
  if (!current || !base || current === base)
    return { color: C.cellText, bgcolor: "transparent" };
  return current > base
    ? { color: C.savingsNeg, bgcolor: "rgba(211,47,47,0.05)" }
    : { color: C.savingsPos, bgcolor: "rgba(46,125,50,0.05)" };
}

const ROW_H: Record<string, number> = {
  "section-header": 48,
  "category-header": 40,
  "column-header": 36,
  service: 32,
  separator: 4,
  "category-total": 32,
  "section-total": 40,
  "grand-total": 48,
};

const INFO_ROWS = [
  { key: "local", label: "LOCAL" },
  { key: "city", label: "CIDADE/UF" },
  { key: "country", label: "PAÍS" },
  { key: "address", label: "ENDEREÇO" },
  { key: "eventPeriods", label: "PERÍODO DE HOSPEDAGEM" },
  { key: "meetingPeriod", label: "PERÍODO DE REUNIÃO" },
  { key: "availability", label: "DISPONIBILIDADE" },
  { key: "deadline", label: "DEADLINE" },
  { key: "site", label: "SITE" },
];

/** Orçamento base (v0), depois todas as negociações por versão, e por último Assessoria (antes de Saving Total). */
function sortNegotiationCompareColumns(
  data: BudgetInfoComparison[]
): BudgetInfoComparison[] {
  return [...data].sort((a, b) => {
    const aBase = a.version === 0;
    const bBase = b.version === 0;
    if (aBase !== bBase) return aBase ? -1 : 1;
    const aCon = Boolean(a.isConsulting);
    const bCon = Boolean(b.isConsulting);
    if (aCon !== bCon) return aCon ? 1 : -1;
    return a.version - b.version;
  });
}

// ─── domain types ─────────────────────────────────────────────────────────────
type VersionEntry = { version: number; label: string; isConsulting: boolean };

type BudgetNegotConfig = {
  budgetId: string;
  budgetName: string;
  budgetIndex: number;
  isExpanded: boolean;
  availableVersions: VersionEntry[];
  selectedVersion: number | null;
  includeConsulting: boolean;
  hasConsultingVersion: boolean;
};

type TabState = {
  budgetId: string;
  budgetName: string;
  budgetIndex: number;
  activeVersions: number[];
  refVersion: number;
  consultingVersion: number | null;
  includeConsulting: boolean;
  sortOrder: string;
  loading: boolean;
  error: string | null;
  data: BudgetInfoComparison[] | null;
  collSec: Set<string>;
  collCat: Set<string>;
};

type TableRow =
  | { kind: "info-row"; fieldKey: string; label: string }
  | { kind: "section-header"; sectionKey: string; label: string }
  | {
      kind: "category-header";
      sectionKey: string;
      categoryKey: string;
      label: string;
    }
  | { kind: "column-header"; sectionKey: string; categoryKey: string }
  | {
      kind: "service";
      sectionKey: string;
      categoryKey: string;
      serviceType: string;
      slot: number;
      label: string;
    }
  | { kind: "separator"; sectionKey: string; categoryKey: string }
  | {
      kind: "category-total";
      sectionKey: string;
      categoryKey: string;
      totalKey: string;
      label: string;
      isLast: boolean;
    }
  | { kind: "section-total"; sectionKey: string; label: string }
  | { kind: "grand-total"; label: string }
  | { kind: "gap"; height: number; sectionKey?: string; inSection: boolean };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** List/summary rows for selection labels + versionHistory — payloads from export API by id. */
  budgetList: ComparisonModalBudgetList;
}

// ─── pure helpers ─────────────────────────────────────────────────────────────
const fmtCurrency = (v?: number | null) => {
  if (v === null || v === undefined || isNaN(v as number)) return "-";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(v as number);
};

const getCatLabel = (key: string) =>
  BudgetItemTypeEnum[key as keyof typeof BudgetItemTypeEnum] || key;

function buildRows(
  data: BudgetInfoComparison[],
  catTotals: ComparisonCategoryTotalRowDef[]
): TableRow[] {
  const rows: TableRow[] = [];
  if (!data?.length) return rows;

  // ── Info header rows (LOCAL, CIDADE/UF, etc.) ──
  INFO_ROWS.forEach(({ key, label }) =>
    rows.push({ kind: "info-row", fieldKey: key, label })
  );
  rows.push({ kind: "gap", height: 16, inSection: false });

  const sectionOrder: string[] = [];
  const sectionTitles = new Map<string, string>();
  const catsBySection = new Map<string, string[]>();

  for (const b of data) {
    // MMV-2419-7: a estrutura de linhas é a UNIÃO do orçamento base (v0) com as
    // versões de negociação. Assim, um serviço/fornecedor que existia no orçamento
    // e foi excluído da negociação continua gerando linha — renderizada zerada e
    // com o savings nas colunas onde não está mais presente (ver getValues/getSavingsRaw).
    for (const sec of b.sections || []) {
      if (!sectionTitles.has(sec.key)) {
        sectionOrder.push(sec.key);
        sectionTitles.set(sec.key, sec.title);
        catsBySection.set(sec.key, []);
      }
      for (const svc of sec.allServices || []) {
        const cats = catsBySection.get(sec.key)!;
        if (!cats.includes(svc.type)) cats.push(svc.type);
      }
    }
  }

  sectionOrder.sort((a, b) => a.localeCompare(b));

  for (const sk of sectionOrder) {
    rows.push({
      kind: "section-header",
      sectionKey: sk,
      label: sectionTitles.get(sk) || sk,
    });
    rows.push({ kind: "gap", height: 8, sectionKey: sk, inSection: true });

    const cats = catsBySection.get(sk) || [];
    cats.forEach((ck, ci) => {
      if (ci > 0)
        rows.push({ kind: "gap", height: 8, sectionKey: sk, inSection: true });
      rows.push({
        kind: "category-header",
        sectionKey: sk,
        categoryKey: ck,
        label: `${ci + 1}   ${getCatLabel(ck)}`,
      });
      rows.push({ kind: "column-header", sectionKey: sk, categoryKey: ck });

      const seenTypes = new Set<string>();
      const orderedTypes: string[] = [];
      for (const b of data) {
        // MMV-2419-7: inclui o v0 para não perder tipos que só existem no orçamento
        // base (serviço removido de todas as negociações).
        const sec = b.sections?.find((s) => s.key === sk);
        for (const svc of sec?.allServices?.filter((sv) => sv.type === ck) ||
          []) {
          const st = resolveServiceValues(svc, b.status, b.version)
            ?.serviceType as string | undefined;
          if (st && !seenTypes.has(st)) {
            seenTypes.add(st);
            orderedTypes.push(st);
          }
        }
      }

      let globalIdx = 0;
      for (const st of orderedTypes) {
        let maxCount = 0;
        for (const b of data) {
          // MMV-2419-7: considera o v0 na contagem de slots para reservar a linha do
          // fornecedor removido (aparece zerado + savings nas colunas de negociação).
          const sec = b.sections?.find((s) => s.key === sk);
          const cnt =
            sec?.allServices?.filter(
              (sv) =>
                sv.type === ck &&
                resolveServiceValues(sv, b.status, b.version)?.serviceType ===
                  st
            ).length ?? 0;
          if (cnt > maxCount) maxCount = cnt;
        }
        const displayName =
          (ServiceOptions as any)[ck]?.find((o: any) => o.value === st)?.text ??
          st;
        for (let slot = 0; slot < maxCount; slot++) {
          rows.push({
            kind: "service",
            sectionKey: sk,
            categoryKey: ck,
            serviceType: st,
            slot,
            label: `${globalIdx + 1} ${displayName}`,
          });
          globalIdx++;
        }
      }

      rows.push({ kind: "separator", sectionKey: sk, categoryKey: ck });
      catTotals.forEach((t, ti) =>
        rows.push({
          kind: "category-total",
          sectionKey: sk,
          categoryKey: ck,
          totalKey: t.key,
          label: t.label,
          isLast: ti === catTotals.length - 1,
        })
      );
    });

    rows.push({ kind: "gap", height: 8, sectionKey: sk, inSection: true });
    rows.push({
      kind: "section-total",
      sectionKey: sk,
      label: `[${sk}] TOTAL`,
    });
    rows.push({ kind: "gap", height: 32, sectionKey: sk, inSection: false });
  }

  rows.push({
    kind: "grand-total",
    label: "RESUMO GERAL DOS CUSTOS (A+B+C+D)",
  });
  return rows;
}

type FiveVals = [
  string,
  string | number,
  string | number,
  string | number,
  string | number,
  string | number,
  string
];

function getInfoValue(b: BudgetInfoComparison, fieldKey: string): string {
  const ci = b.comparisonInfo as any;
  if (fieldKey === "eventPeriods") {
    const periods = ci?.eventPeriods as
      | Array<{ inicio: string; fim: string }>
      | undefined;
    if (periods?.length) {
      return periods
        .map((p) => {
          try {
            const fmt = (iso: string) =>
              new Date(iso).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });
            return `${fmt(p.inicio)} à ${fmt(p.fim)}`;
          } catch {
            return `${p.inicio} à ${p.fim}`;
          }
        })
        .join(" | ");
    }
    return ci?.accommodationPeriod || "-";
  }
  return ci?.[fieldKey] || "-";
}

/** Assessoria fora dos totais; fallback pelo nome se isConsulting não vier da API. */
function isConsultingNegotiationColumn(bd: BudgetInfoComparison): boolean {
  if (bd.isConsulting) return true;
  return (bd.budgetName || "").toLowerCase().includes("assessoria");
}

function getValues(
  b: BudgetInfoComparison,
  row: TableRow,
  columnData: BudgetInfoComparison[],
  bdIndex: number,
  taxFlags: { showIssSummary: boolean; showIofSummary: boolean }
): FiveVals {
  const savingsFmt = (isServiceSpecial?: boolean) => {
    if (b.version === 0) return fmtCurrency(0);
    if (isServiceSpecial) return "-";
    // Assessoria: delta vs coluna anterior (igual export); não entra só em sumSavingsForSavingTotal.
    return fmtCurrency(getSavingsRaw(columnData, bdIndex, row, taxFlags));
  };

  if (row.kind === "info-row") {
    return ["", "", "", "", getInfoValue(b, row.fieldKey), "", ""];
  }
  if (row.kind === "section-header") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    return [
      "",
      "-",
      "-",
      "-",
      fmtCurrency((s?.sectionTotals as any)?.totals?.totalInBRL),
      savingsFmt(),
      "",
    ];
  }
  if (row.kind === "service") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    const sameType =
      s?.allServices?.filter(
        (x) =>
          x.type === row.categoryKey &&
          resolveServiceValues(x, b.status, b.version)?.serviceType ===
            row.serviceType
      ) ?? [];
    const svc = sameType[row.slot];
    if (!svc)
      return ["-", 0, 0, fmtCurrency(0), fmtCurrency(0), savingsFmt(), "-"];
    const sv = resolveServiceValues(svc, b.status, b.version);
    const typeTax = sv?.taxData?.tariff?.typeTax;
    const isCortesy = typeTax === "CORTESY";
    const isIncluso = typeTax === "INCLUDED";
    const isAcClient = typeTax === "AC_CLIENT";
    const special = isCortesy
      ? "Cortesia"
      : isIncluso
      ? "Incluso"
      : "A/C Cliente";
    const vuCell = getComparisonServiceValorUnitarioDisplay(sv, true);
    const valorUn =
      isCortesy || isIncluso || isAcClient
        ? special
        : typeof vuCell === "number"
        ? fmtCurrency(vuCell)
        : vuCell;
    const vtCell = getComparisonServiceValorTotalDisplay(sv, true);
    const valorTotal =
      isCortesy || isIncluso || isAcClient
        ? special
        : typeof vtCell === "number" && Number.isFinite(vtCell)
        ? fmtCurrency(vtCell)
        : typeof vtCell === "string"
        ? vtCell
        : fmtCurrency(0);
    const savings = savingsFmt(isCortesy || isIncluso || isAcClient);
    // Descrição do serviço (campo voltado ao cliente) — igual à coluna
    // "Descrição" do Excel comparativo; distinta de "Obs. Internas".
    return [
      sv?.observation || "-",
      sv?.quantity ?? 0,
      sv?.daily ?? 0,
      valorUn,
      valorTotal,
      savings,
      sv?.internalObservation || "-",
    ];
  }
  if (row.kind === "category-total") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    const ct = (s?.totalsPerCategory as any)?.[row.categoryKey];
    const vuRaw = getExportParityCategorySummaryVu(
      row.totalKey,
      b,
      row.sectionKey,
      row.categoryKey
    );
    const valorUn = vuRaw === "" ? "" : vuRaw;
    const vt = getExportParityCategorySummaryTotalBrl(
      row.totalKey,
      ct,
      taxFlags
    );
    return ["", "-", "-", valorUn, fmtCurrency(vt), savingsFmt(), ""];
  }
  if (row.kind === "section-total") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    return [
      "",
      "-",
      "-",
      "-",
      fmtCurrency((s?.sectionTotals as any)?.totals?.totalInBRL),
      savingsFmt(),
      "",
    ];
  }
  if (row.kind === "grand-total") {
    return [
      "",
      "-",
      "-",
      "-",
      fmtCurrency((b.totals as any)?.totals?.totals?.totalInBRL),
      savingsFmt(),
      "",
    ];
  }
  return ["", "", "", "", "", "", ""];
}

function getTotalRaw(
  bd: BudgetInfoComparison,
  row: TableRow,
  taxFlags: { showIssSummary: boolean; showIofSummary: boolean }
): number {
  if (row.kind === "section-header") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    return (s?.sectionTotals as any)?.totals?.totalInBRL ?? 0;
  }
  if (row.kind === "service") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    const sameType =
      s?.allServices?.filter(
        (x) =>
          x.type === row.categoryKey &&
          resolveServiceValues(x, bd.status, bd.version)?.serviceType ===
            row.serviceType
      ) ?? [];
    const svc = sameType[row.slot];
    if (!svc) return 0;
    const sv = resolveServiceValues(svc, bd.status, bd.version);
    const typeTax = sv?.taxData?.tariff?.typeTax;
    if (
      typeTax === "CORTESY" ||
      typeTax === "INCLUDED" ||
      typeTax === "AC_CLIENT"
    )
      return 0;
    const vt = getComparisonServiceValorTotalDisplay(sv, true);
    return typeof vt === "number" && Number.isFinite(vt) ? vt : 0;
  }
  if (row.kind === "category-total") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    const ct = (s?.totalsPerCategory as any)?.[row.categoryKey];
    return getExportParityCategorySummaryTotalBrl(row.totalKey, ct, taxFlags);
  }
  if (row.kind === "section-total") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    return (s?.sectionTotals as any)?.totals?.totalInBRL ?? 0;
  }
  if (row.kind === "grand-total") {
    return (bd.totals as any)?.totals?.totals?.totalInBRL ?? 0;
  }
  return 0;
}

/** Orçamento base — referência do Saving da Assessoria (igual populateComparative no Excel). */
function compareBaselineBudgetNegotiation(
  columnData: BudgetInfoComparison[]
): BudgetInfoComparison | undefined {
  return columnData.find(
    (b) => b.version === 0 || (b.status || "").toUpperCase() === "OPEN"
  );
}

function previousBudgetForSavingsNegotiation(
  columnData: BudgetInfoComparison[],
  bdIndex: number
): BudgetInfoComparison | undefined {
  const bd = columnData[bdIndex];
  if (isConsultingNegotiationColumn(bd))
    return compareBaselineBudgetNegotiation(columnData);
  return bdIndex > 0 ? columnData[bdIndex - 1] : undefined;
}

function getSavingsRaw(
  columnData: BudgetInfoComparison[],
  bdIndex: number,
  row: TableRow,
  taxFlags: { showIssSummary: boolean; showIofSummary: boolean }
): number {
  if (bdIndex <= 0) return 0;
  const bd = columnData[bdIndex];
  if (bd.version === 0) return 0;
  const prev = previousBudgetForSavingsNegotiation(columnData, bdIndex);
  if (!prev) return 0;
  if (row.kind === "service") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    const sameType =
      s?.allServices?.filter(
        (x) =>
          x.type === row.categoryKey &&
          resolveServiceValues(x, bd.status, bd.version)?.serviceType ===
            row.serviceType
      ) ?? [];
    const svc = sameType[row.slot];
    if (!svc)
      return getTotalRaw(bd, row, taxFlags) - getTotalRaw(prev, row, taxFlags);
    const sv = resolveServiceValues(svc, bd.status, bd.version);
    const typeTax = sv?.taxData?.tariff?.typeTax;
    if (
      typeTax === "CORTESY" ||
      typeTax === "INCLUDED" ||
      typeTax === "AC_CLIENT"
    )
      return 0;
  }
  return getTotalRaw(bd, row, taxFlags) - getTotalRaw(prev, row, taxFlags);
}

/** Saving Total: soma só negociações do fluxo (célula Savings da assessoria segue exibida, mas não entra na soma). */
function sumSavingsForSavingTotal(
  columnData: BudgetInfoComparison[],
  row: TableRow,
  taxFlags: { showIssSummary: boolean; showIofSummary: boolean }
): number {
  return columnData.reduce((sum, bd, bi) => {
    if (isConsultingNegotiationColumn(bd)) return sum;
    return sum + getSavingsRaw(columnData, bi, row, taxFlags);
  }, 0);
}

function isRowVisible(
  row: TableRow,
  collSec: Set<string>,
  collCat: Set<string>
): boolean {
  if (
    row.kind === "grand-total" ||
    row.kind === "section-header" ||
    row.kind === "info-row"
  )
    return true;
  if (row.kind === "column-header") {
    const sk = row.sectionKey;
    if (sk && collSec.has(sk)) return false;
    const ck = row.categoryKey;
    if (sk && ck && collCat.has(`${sk}||${ck}`)) return false;
    return true;
  }
  if (row.kind === "gap") {
    if (!row.inSection) return true;
    return !(row.sectionKey && collSec.has(row.sectionKey));
  }
  const sk = (row as any).sectionKey as string | undefined;
  if (sk && collSec.has(sk)) return false;
  if (row.kind === "category-header") return true;
  const ck = (row as any).categoryKey as string | undefined;
  if (sk && ck && collCat.has(`${sk}||${ck}`)) return false;
  return true;
}

function getColLabel(b: BudgetInfoComparison): string {
  if (b.version === 0) return b.budgetName || "Orçamento";
  if (b.isConsulting) return "Assessoria";
  const negN = b.hasConsultingBefore ? b.version - 1 : b.version;
  return `Negociação ${negN}`;
}

// ─── main component ───────────────────────────────────────────────────────────
const NegotiationComparisonModal: React.FC<Props> = ({
  isOpen,
  onClose,
  budgetList,
}) => {
  const [step, setStep] = useState<"selection" | "comparison">("selection");
  const [configs, setConfigs] = useState<BudgetNegotConfig[]>([]);
  const [tabs, setTabs] = useState<TabState[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep("selection");
      setConfigs([]);
      setTabs([]);
      setActiveTab(0);
      setAdvancing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const built: BudgetNegotConfig[] = [];
    (budgetList || []).forEach((budget, bi) => {
      const negVersions: VersionEntry[] = [];
      let consultingCount = 0;
      let hasConsultingVersion = false;
      for (const vh of budget.versionHistory || []) {
        if (vh.version === 0) continue;
        if (vh.isConsulting) {
          consultingCount++;
          hasConsultingVersion = true;
          continue;
        }
        if (vh.status !== "NEGOTIATE") continue;
        const negN =
          consultingCount > 0 ? vh.version - consultingCount : vh.version;
        negVersions.push({
          version: vh.version,
          label: `Negociação ${negN}`,
          isConsulting: false,
        });
      }
      if (negVersions.length === 0) return;
      built.push({
        budgetId: budget._id || "",
        budgetName: comparisonBudgetDisplayName(budget, bi),
        budgetIndex: budget.budgetIndex ?? bi,
        isExpanded: true,
        availableVersions: negVersions,
        selectedVersion: null,
        includeConsulting: false,
        hasConsultingVersion,
      });
    });
    setConfigs(built);
  }, [isOpen, budgetList]);

  const hasAnySelection = useMemo(
    () => configs.some((c) => c.selectedVersion !== null),
    [configs]
  );

  const updateConfig = (budgetId: string, patch: Partial<BudgetNegotConfig>) =>
    setConfigs((prev) =>
      prev.map((c) => (c.budgetId === budgetId ? { ...c, ...patch } : c))
    );

  const resetSelection = () =>
    setConfigs((prev) =>
      prev.map((c) => ({
        ...c,
        selectedVersion: null,
        includeConsulting: false,
      }))
    );

  const buildInitialTabStates = useCallback((): TabState[] => {
    return configs
      .filter((c) => c.selectedVersion !== null)
      .map((c) => {
        const selectedV = c.selectedVersion!;
        const budgetHistory = comparisonBudgetVersionHistory(
          findComparisonBudget(budgetList, c.budgetId)
        );

        const consultingEntry = budgetHistory.find((vh) => vh.isConsulting);
        const consultingVersion: number | null =
          consultingEntry?.version ?? null;

        // refVersion = the version the user selected — API fetches data up to this version.
        // activeVersions includes all negotiation versions from 1 up to selectedV (full history).
        const negVersionsUpTo = budgetHistory
          .filter(
            (vh) =>
              !vh.isConsulting && vh.version > 0 && vh.version <= selectedV
          )
          .map((vh) => vh.version as number)
          .sort((a: number, b: number) => a - b);

        // Negociações em ordem de versão; assessoria sempre por último (não ordenar só por número).
        const activeVersions = [
          ...negVersionsUpTo,
          ...(c.includeConsulting && consultingVersion != null
            ? [consultingVersion]
            : []),
        ];

        return {
          budgetId: c.budgetId,
          budgetName: c.budgetName,
          budgetIndex: c.budgetIndex,
          activeVersions,
          refVersion: selectedV,
          consultingVersion,
          includeConsulting: c.includeConsulting,
          sortOrder: "DEFAULT",
          loading: false,
          error: null,
          data: null,
          collSec: new Set<string>(),
          collCat: new Set<string>(),
        };
      });
  }, [configs, budgetList]);

  const fetchTabData = useCallback(
    async (tab: TabState, updater: (patch: Partial<TabState>) => void) => {
      updater({ loading: true, error: null });
      try {
        // Backend includes the assessoria column only when exportType === "CLIENT_CONSULTING".
        // The backend ignores any includeConsulting/consultingVersion fields in the request body.
        const exportType = tab.includeConsulting
          ? "CLIENT_CONSULTING"
          : "CLIENT";

        const raw: BudgetInfoComparison[] =
          await BudgetService.generateNegotiationComparisonInfo({
            exportType,
            sortOrder: tab.sortOrder,
            budget: {
              budgetId: tab.budgetId,
              negotiationVersion: tab.refVersion,
            },
          });

        // Keep base (v=0), negotiations up to refVersion, and consulting if requested.
        const data = (raw || []).filter((bd) => {
          if (bd.version === 0) return true;
          if (bd.isConsulting) return tab.includeConsulting;
          return bd.version <= tab.refVersion;
        });

        updater({ data, loading: false });
      } catch {
        updater({
          error: "Não foi possível carregar a comparação. Tente novamente.",
          loading: false,
        });
      }
    },
    []
  );

  const handleAdvance = async () => {
    if (!hasAnySelection) return;
    setAdvancing(true);
    const initialTabs = buildInitialTabStates();
    setTabs(initialTabs);
    setActiveTab(0);
    setStep("comparison");
    setAdvancing(false);
    initialTabs.forEach((tab, i) =>
      fetchTabData(tab, (patch) =>
        setTabs((prev) =>
          prev.map((t, ti) => (ti === i ? { ...t, ...patch } : t))
        )
      )
    );
  };

  const updateTab = (tabIndex: number, patch: Partial<TabState>) =>
    setTabs((prev) =>
      prev.map((t, i) => (i === tabIndex ? { ...t, ...patch } : t))
    );

  const refreshTab = (tabIndex: number, tab: TabState) =>
    fetchTabData(tab, (patch) =>
      setTabs((prev) =>
        prev.map((t, i) => (i === tabIndex ? { ...t, ...patch } : t))
      )
    );

  // Called when user picks a different negotiation via radio select in comparison step.
  // Includes all negotiation versions from 1 up to refVersion (like the history of that selection).
  const handleRefreshTab = (tabIndex: number, refVersion: number) => {
    const existing = tabs[tabIndex];

    // Collect all regular negotiation versions <= refVersion from versionHistory
    const budgetHistory = comparisonBudgetVersionHistory(
      findComparisonBudget(budgetList, existing.budgetId)
    );
    const negVersionsUpTo = budgetHistory
      .filter(
        (vh) => !vh.isConsulting && vh.version > 0 && vh.version <= refVersion
      )
      .map((vh) => vh.version as number)
      .sort((a: number, b: number) => a - b);

    const consultingVersions =
      existing.includeConsulting && existing.consultingVersion != null
        ? [existing.consultingVersion]
        : [];

    const updated: TabState = {
      ...existing,
      refVersion,
      activeVersions: [...negVersionsUpTo, ...consultingVersions],
    };
    setTabs((prev) => prev.map((t, i) => (i === tabIndex ? updated : t)));
    fetchTabData(updated, (patch) =>
      setTabs((prev) =>
        prev.map((t, i) => (i === tabIndex ? { ...t, ...patch } : t))
      )
    );
  };

  const handleToggleConsulting = (tabIndex: number, checked: boolean) => {
    const tab = tabs[tabIndex];

    // find the consulting version number in this budget's versionHistory
    const budgetHistory = comparisonBudgetVersionHistory(
      findComparisonBudget(budgetList, tab.budgetId)
    );
    const consultingEntry = budgetHistory.find((vh) => vh.isConsulting);
    const consultingVer: number | null = consultingEntry?.version ?? null;

    // keep activeVersions in sync with the consulting toggle for chip display
    let newActiveVersions = tab.activeVersions;
    if (consultingEntry) {
      if (checked && !tab.activeVersions.includes(consultingEntry.version)) {
        newActiveVersions = [...tab.activeVersions, consultingEntry.version];
      } else if (!checked) {
        newActiveVersions = tab.activeVersions.filter(
          (v) => v !== consultingEntry.version
        );
      }
    }

    const updated = {
      ...tab,
      includeConsulting: checked,
      consultingVersion: consultingVer,
      activeVersions: newActiveVersions,
    };
    updateTab(tabIndex, {
      includeConsulting: checked,
      consultingVersion: consultingVer,
      activeVersions: newActiveVersions,
    });
    refreshTab(tabIndex, updated);
  };

  const handleRemoveVersion = (tabIndex: number, version: number) => {
    const tab = tabs[tabIndex];
    if (tab.activeVersions.length <= 2) return;

    const budget = findComparisonBudget(budgetList, tab.budgetId);
    const vhEntry = comparisonBudgetVersionHistory(budget).find(
      (vh) => vh.version === version
    );
    const removingConsulting = !!vhEntry?.isConsulting;

    const newVersions = tab.activeVersions.filter((v) => v !== version);
    const newRef =
      version === tab.refVersion
        ? Math.max(...newVersions.filter((v) => v > 0))
        : tab.refVersion;

    const updated = {
      ...tab,
      activeVersions: newVersions,
      refVersion: newRef,
      includeConsulting: removingConsulting ? false : tab.includeConsulting,
    };
    updateTab(tabIndex, updated);
    refreshTab(tabIndex, updated);
  };

  const handleRetry = (tabIndex: number) =>
    refreshTab(tabIndex, tabs[tabIndex]);

  const getVersionChipLabel = (tab: TabState, version: number): string => {
    if (version === 0) return "Orçamento";
    const budget = findComparisonBudget(budgetList, tab.budgetId);
    const history = comparisonBudgetVersionHistory(budget);
    const vhEntry = history.find((vh) => vh.version === version);
    if (vhEntry?.isConsulting) return "Assessoria";
    const consultingBefore = history.filter(
      (vh) => vh.isConsulting && vh.version < version
    ).length;
    return `Negociação ${version - consultingBefore}`;
  };

  const isSelectionStep = step === "selection";

  // ── Selection dialog ────────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={isOpen && isSelectionStep}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 680,
            maxWidth: "95vw",
            borderRadius: "8px",
            boxShadow: "0px 4px 18px rgba(75,70,92,0.1)",
          },
        }}
      >
        <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 24,
                  fontWeight: 400,
                  color: C.titleText,
                  lineHeight: "32px",
                  fontFamily: FONT,
                }}
              >
                Pré-visualizar negociações
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{ color: C.titleText }}
              >
                <IoMdClose size={20} />
              </IconButton>
            </Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 400,
                color: C.descText,
                lineHeight: "22px",
                fontFamily: FONT,
              }}
            >
              Selecione abaixo as negociações desejadas. Cada orçamento terá sua
              própria aba de comparativos.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: "60vh",
              overflowY: "auto",
              pr: 0.5,
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,0,0,0.2)",
                borderRadius: 3,
              },
            }}
          >
            {configs.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ py: 3, textAlign: "center" }}
              >
                Nenhum orçamento com negociações disponíveis.
              </Typography>
            ) : (
              configs.map((cfg) => (
                <BudgetCard
                  key={cfg.budgetId}
                  cfg={cfg}
                  onUpdateConfig={updateConfig}
                />
              ))
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="text"
              onClick={resetSelection}
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: C.linkBlue,
                textTransform: "none",
                px: 1.25,
                height: 40,
                fontFamily: FONT,
                "&:hover": { bgcolor: "rgba(25,118,210,0.04)" },
              }}
            >
              Redefinir seleção
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: C.linkBlue,
                borderColor: C.primary,
                textTransform: "none",
                px: 1.25,
                borderRadius: "8px",
                height: 40,
                fontFamily: FONT,
                "&:hover": {
                  borderColor: C.primary,
                  bgcolor: "rgba(19,97,164,0.04)",
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleAdvance}
              disabled={!hasAnySelection || advancing}
              startIcon={
                advancing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
              sx={{
                fontWeight: 600,
                fontSize: 14,
                bgcolor: C.primary,
                textTransform: "none",
                px: 1.25,
                borderRadius: "8px",
                height: 40,
                fontFamily: FONT,
                "&:hover": { bgcolor: "#0f4f89" },
                "&.Mui-disabled": { bgcolor: "#a0bfd8", color: "#fff" },
              }}
            >
              Avançar
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Comparison dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={isOpen && !isSelectionStep}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": { margin: "56px" },
          "& .MuiDialog-container": { padding: 0 },
        }}
        PaperProps={{
          sx: {
            width: "calc(100% - 112px)",
            maxWidth: "calc(100% - 112px)",
            height: "calc(100% - 112px)",
            maxHeight: "calc(100% - 112px)",
            borderRadius: "8px",
            boxShadow: "0px 4px 18px rgba(75,70,92,0.1)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <ComparisonStep
          tabs={tabs}
          activeTab={activeTab}
          budgetList={budgetList}
          onChangeTab={(_, v) => setActiveTab(v)}
          onToggleConsulting={handleToggleConsulting}
          onRemoveVersion={handleRemoveVersion}
          onRetry={handleRetry}
          onBack={() => setStep("selection")}
          onClose={onClose}
          getVersionChipLabel={getVersionChipLabel}
          updateTab={updateTab}
          onRefreshTab={handleRefreshTab}
        />
      </Dialog>
    </>
  );
};

// ─── BudgetCard (selection step) ──────────────────────────────────────────────
interface BudgetCardProps {
  cfg: BudgetNegotConfig;
  onUpdateConfig: (budgetId: string, patch: Partial<BudgetNegotConfig>) => void;
}

const BudgetCard: React.FC<BudgetCardProps> = ({ cfg, onUpdateConfig }) => (
  <Box
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: 2,
      bgcolor: "#fcfcfc",
      p: 2,
      display: "flex",
      flexDirection: "column",
      gap: cfg.isExpanded ? 2 : 0,
    }}
  >
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
      onClick={() =>
        onUpdateConfig(cfg.budgetId, { isExpanded: !cfg.isExpanded })
      }
    >
      <Box
        sx={{
          width: 20,
          height: 20,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {cfg.isExpanded ? (
          <MdKeyboardArrowDown size={20} color={C.cellText} />
        ) : (
          <MdKeyboardArrowUp size={20} color={C.cellText} />
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: 15,
          color: C.cellText,
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {cfg.budgetName}
      </Typography>
    </Box>

    {cfg.isExpanded && (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pl: 2 }}>
        {cfg.availableVersions.map((v) => (
          <Box
            key={v.version}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onUpdateConfig(cfg.budgetId, {
                selectedVersion:
                  cfg.selectedVersion === v.version ? null : v.version,
              });
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                flexShrink: 0,
                border:
                  cfg.selectedVersion === v.version
                    ? `5px solid ${C.primary}`
                    : "1.5px solid #b7b5be",
                bgcolor: "#fff",
                boxShadow:
                  cfg.selectedVersion === v.version
                    ? "0px 2px 4px rgba(165,163,174,0.3)"
                    : "none",
              }}
            />
            <Typography
              sx={{ fontFamily: FONT, fontSize: 14, color: C.cellText }}
            >
              {v.label}
            </Typography>
          </Box>
        ))}

        <Tooltip
          title={
            !cfg.hasConsultingVersion
              ? "Este orçamento não possui versão de assessoria"
              : ""
          }
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              opacity: cfg.hasConsultingVersion ? 1 : 0.45,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={cfg.includeConsulting}
              disabled={!cfg.hasConsultingVersion}
              onChange={(e) =>
                onUpdateConfig(cfg.budgetId, {
                  includeConsulting: e.target.checked,
                })
              }
              size="small"
              sx={{
                p: 0,
                "& .MuiSvgIcon-root": { fontSize: 18, color: C.primary },
              }}
            />
            <Typography
              sx={{ fontFamily: FONT, fontSize: 14, color: C.cellText }}
            >
              Incluir versão assessoria
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    )}
  </Box>
);

// ─── ComparisonStep ────────────────────────────────────────────────────────────
interface ComparisonStepProps {
  tabs: TabState[];
  activeTab: number;
  budgetList: ComparisonModalBudgetList;
  onChangeTab: (event: React.SyntheticEvent, value: number) => void;
  onToggleConsulting: (tabIndex: number, checked: boolean) => void;
  onRemoveVersion: (tabIndex: number, version: number) => void;
  onRetry: (tabIndex: number) => void;
  onBack: () => void;
  onClose: () => void;
  getVersionChipLabel: (tab: TabState, version: number) => string;
  updateTab: (tabIndex: number, patch: Partial<TabState>) => void;
  onRefreshTab: (tabIndex: number, refVersion: number) => void;
}

const ComparisonStep: React.FC<ComparisonStepProps> = ({
  tabs,
  activeTab,
  budgetList,
  onChangeTab,
  onToggleConsulting,
  onRemoveVersion,
  onRetry,
  onBack,
  onClose,
  getVersionChipLabel,
  updateTab,
  onRefreshTab,
}) => {
  const tab = tabs[activeTab];
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);
  const dropdownAnchorRef = useRef<HTMLDivElement>(null);

  const tabHasConsulting = useMemo(() => {
    if (!tab) return false;
    return comparisonBudgetVersionHistory(
      findComparisonBudget(budgetList, tab.budgetId)
    ).some((vh) => vh.isConsulting);
  }, [tab, budgetList]);

  // All negotiation versions available for this tab's budget (from budgetList)
  const selectableVersions = useMemo(() => {
    if (!tab) return [];
    return comparisonBudgetVersionHistory(
      findComparisonBudget(budgetList, tab.budgetId)
    )
      .filter(
        (vh) =>
          vh.version !== 0 && !vh.isConsulting && vh.status === "NEGOTIATE"
      )
      .sort((a, b) => a.version - b.version);
  }, [tab, budgetList]);

  // Select a single negotiation version → re-fetch like selecting in the previous step
  const handleSelectVersion = useCallback(
    (version: number) => {
      if (!tab || tab.refVersion === version) return;
      setVersionDropdownOpen(false);
      onRefreshTab(activeTab, version);
    },
    [tab, activeTab, onRefreshTab]
  );

  // Close dropdown on outside click
  useEffect(() => {
    if (!versionDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        dropdownAnchorRef.current &&
        !dropdownAnchorRef.current.contains(e.target as Node)
      ) {
        setVersionDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [versionDropdownOpen]);

  return (
    <>
      {/* header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 4,
          pt: 4,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 400,
            color: C.titleText,
            lineHeight: "32px",
            fontFamily: FONT,
          }}
        >
          Pré-visualizar negociações
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: C.titleText }}>
          <IoMdClose size={20} />
        </IconButton>
      </Box>

      {/* tabs */}
      <Box sx={{ px: 4, pt: 2, flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={onChangeTab}
          sx={{
            borderBottom: bdr,
            minHeight: 48,
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: FONT,
              fontSize: 14,
              color: C.titleText,
              minHeight: 48,
            },
            "& .Mui-selected": { color: C.primary, fontWeight: 600 },
            "& .MuiTabs-indicator": { bgcolor: C.primary },
          }}
        >
          {tabs.map((t, i) => (
            <Tab key={t.budgetId} label={t.budgetName} value={i} />
          ))}
        </Tabs>
      </Box>

      {/* controls */}
      {tab && (
        <Box
          sx={{
            px: 4,
            pt: 2,
            pb: 2,
            display: "flex",
            gap: 1,
            alignItems: "flex-end",
            flexShrink: 0,
            opacity: tab.loading ? 0.6 : 1,
            pointerEvents: tab.loading ? "none" : "auto",
          }}
        >
          {/* Version selector dropdown */}
          <Box
            ref={dropdownAnchorRef}
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              position: "relative",
            }}
          >
            <Typography
              sx={{ fontSize: 13, color: C.cellText, fontFamily: FONT }}
            >
              Comparar
            </Typography>
            {/* Trigger */}
            <Box
              onClick={() => setVersionDropdownOpen((o) => !o)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: C.white,
                border: `1px solid ${
                  versionDropdownOpen ? C.primary : C.controlBorder
                }`,
                borderRadius: "6px",
                minHeight: 36,
                px: 1.5,
                py: 0.5,
                cursor: "pointer",
                userSelect: "none",
                transition: "border-color 0.15s",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  color: tab.refVersion ? C.cellText : C.descText,
                  fontFamily: FONT,
                  flex: 1,
                }}
              >
                {tab.refVersion
                  ? getVersionChipLabel(tab, tab.refVersion)
                  : "Selecione uma negociação..."}
              </Typography>
              <MdKeyboardArrowDown
                size={18}
                color={C.descText}
                style={{
                  flexShrink: 0,
                  transform: versionDropdownOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            </Box>

            {/* Dropdown with radio buttons */}
            {versionDropdownOpen && (
              <Paper
                elevation={4}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  mt: "4px",
                  zIndex: 20,
                  borderRadius: "8px",
                  overflow: "hidden",
                  maxHeight: 260,
                  overflowY: "auto",
                }}
              >
                {selectableVersions.map((vh) => {
                  const isSelected = tab.refVersion === vh.version;
                  return (
                    <Box
                      key={vh.version}
                      onClick={() => handleSelectVersion(vh.version)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        cursor: "pointer",
                        bgcolor: isSelected
                          ? "rgba(19,97,164,0.06)"
                          : "transparent",
                        "&:hover": {
                          bgcolor: isSelected
                            ? "rgba(19,97,164,0.10)"
                            : "#f5f5f5",
                        },
                      }}
                    >
                      {/* Radio circle */}
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          flexShrink: 0,
                          border: isSelected
                            ? `5px solid ${C.primary}`
                            : `1.5px solid #b7b5be`,
                          bgcolor: "#fff",
                          boxShadow: isSelected
                            ? "0px 2px 4px rgba(165,163,174,0.3)"
                            : "none",
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: 14,
                          color: C.cellText,
                        }}
                      >
                        {getVersionChipLabel(tab, vh.version)}
                      </Typography>
                    </Box>
                  );
                })}
              </Paper>
            )}
          </Box>

          <Tooltip
            title={
              !tabHasConsulting
                ? "Este orçamento não possui versão de assessoria"
                : ""
            }
          >
            <span>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tab.includeConsulting}
                    onChange={(e) =>
                      onToggleConsulting(activeTab, e.target.checked)
                    }
                    disabled={tab.loading || !tabHasConsulting}
                    size="small"
                    sx={{
                      "& .MuiSvgIcon-root": { fontSize: 18, color: C.primary },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: 14,
                      color: tabHasConsulting ? C.cellText : C.descText,
                    }}
                  >
                    Incluir versão assessoria
                  </Typography>
                }
                sx={{
                  m: 0,
                  flexShrink: 0,
                  opacity: tabHasConsulting ? 1 : 0.5,
                }}
              />
            </span>
          </Tooltip>
        </Box>
      )}

      {/* table area */}
      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {tab && (
          <>
            {tab.loading && (
              <Box sx={{ p: 4 }}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    height={40}
                    sx={{ mb: 1, borderRadius: 0.5 }}
                  />
                ))}
              </Box>
            )}
            {!tab.loading && tab.error && (
              <Alert
                severity="error"
                sx={{ m: 2, flexShrink: 0 }}
                action={
                  <Button size="small" onClick={() => onRetry(activeTab)}>
                    Tentar novamente
                  </Button>
                }
              >
                {tab.error}
              </Alert>
            )}
            {!tab.loading && !tab.error && tab.data && (
              <NegotiationTable
                data={tab.data.filter(
                  (bd) =>
                    bd.version === 0 || tab.activeVersions.includes(bd.version)
                )}
                collSec={tab.collSec}
                collCat={tab.collCat}
                onToggleSec={(sk) =>
                  updateTab(activeTab, {
                    collSec: new Set(
                      tab.collSec.has(sk)
                        ? Array.from(tab.collSec).filter((x) => x !== sk)
                        : [...Array.from(tab.collSec), sk]
                    ),
                  })
                }
                onToggleCat={(sKey, cKey) => {
                  const k = `${sKey}||${cKey}`;
                  updateTab(activeTab, {
                    collCat: new Set(
                      tab.collCat.has(k)
                        ? Array.from(tab.collCat).filter((x) => x !== k)
                        : [...Array.from(tab.collCat), k]
                    ),
                  });
                }}
              />
            )}
            {!tab.loading && !tab.error && !tab.data && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                }}
              >
                <CircularProgress size={32} sx={{ color: C.primary }} />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* footer */}
      <Box
        sx={{
          px: 4,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          borderTop: bdr,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            fontWeight: 600,
            fontSize: 14,
            color: C.linkBlue,
            borderColor: C.primary,
            textTransform: "none",
            px: 1.25,
            borderRadius: "8px",
            height: 40,
            fontFamily: FONT,
            "&:hover": {
              borderColor: C.primary,
              bgcolor: "rgba(19,97,164,0.04)",
            },
          }}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            fontWeight: 600,
            fontSize: 14,
            bgcolor: C.primary,
            textTransform: "none",
            px: 1.25,
            borderRadius: "8px",
            height: 40,
            fontFamily: FONT,
            "&:hover": { bgcolor: "#0f4f89" },
          }}
        >
          Concluir
        </Button>
      </Box>
    </>
  );
};

// ─── NegotiationTable (mirrors BudgetComparisonModal table) ───────────────────
interface NegotiationTableProps {
  data: BudgetInfoComparison[];
  collSec: Set<string>;
  collCat: Set<string>;
  onToggleSec: (sk: string) => void;
  onToggleCat: (sKey: string, cKey: string) => void;
}

const NegotiationTable: React.FC<NegotiationTableProps> = ({
  data,
  collSec,
  collCat,
  onToggleSec,
  onToggleCat,
}) => {
  const sortedData = useMemo(() => sortNegotiationCompareColumns(data), [data]);

  const comparisonTaxFlags = useMemo(
    () => getComparisonSummaryTaxFlags(sortedData),
    [sortedData]
  );
  const categoryTotalRows = useMemo(
    () => buildComparisonCategoryTotalRows(comparisonTaxFlags),
    [comparisonTaxFlags]
  );
  const tableRows = useMemo(
    () => buildRows(sortedData, categoryTotalRows),
    [sortedData, categoryTotalRows]
  );
  const firstSectionHeaderRowIndex = useMemo(
    () => tableRows.findIndex((r) => r.kind === "section-header"),
    [tableRows]
  );
  const dataColsW = useMemo(
    () =>
      sortedData.reduce(
        (sum, bd) =>
          sum + colWidthsForNegotiationBudget(bd).reduce((a, w) => a + w, 0),
        0
      ),
    [sortedData]
  );
  const tableW = LABEL_W + dataColsW + SAVING_TOTAL_W;
  const totalCols =
    1 +
    sortedData.reduce((sum, bd) => sum + colCountForNegotiationBudget(bd), 0) +
    1;

  return (
    <Box sx={{ flex: 1, overflow: "auto" }}>
      {tableRows.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="textSecondary">
            Nenhum dado disponível.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "inline-block",
            marginLeft: "24px",
            marginTop: "12px",
            marginBottom: "12px",
            borderRadius: "10px",
            overflow: "clip",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.10)",
          }}
        >
          <Box
            component="table"
            sx={{
              tableLayout: "fixed",
              borderCollapse: "collapse",
              width: tableW,
              fontFamily: FONT,
            }}
          >
            {/* colgroup */}
            <colgroup>
              <col style={{ width: LABEL_W }} />
              {sortedData.flatMap((bd, bi) =>
                colWidthsForNegotiationBudget(bd).map((w, ci) => (
                  <col key={`${bi}-${ci}`} style={{ width: w }} />
                ))
              )}
              <col style={{ width: SAVING_TOTAL_W }} />
            </colgroup>

            {/* thead: negotiation version names */}
            <Box component="thead">
              <Box component="tr">
                <Box
                  component="th"
                  sx={{
                    position: "sticky",
                    top: 0,
                    left: 0,
                    zIndex: 12,
                    bgcolor: C.white,
                    height: HEADER_H,
                    borderBottom: bdr,
                    borderRight: bdr,
                  }}
                />
                {sortedData.map((bd) => (
                  <Box
                    component="th"
                    key={`${bd.budgetId}-${bd.version}`}
                    colSpan={colCountForNegotiationBudget(bd)}
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      bgcolor: C.white,
                      height: HEADER_H,
                      borderTop: bdr,
                      borderBottom: bdr,
                      borderLeft: "none",
                      borderRight: GROUP_DIVIDER_R,
                      textAlign: "center",
                      fontSize: 15,
                      fontWeight: 700,
                      color: C.cellText,
                      fontFamily: FONT,
                    }}
                  >
                    {getColLabel(bd)}
                  </Box>
                ))}
                {/* Saving Total — sem borderLeft extra (alinha com divisória do último bloco) */}
                <Box
                  component="th"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    bgcolor: "#e8f5e9",
                    height: HEADER_H,
                    borderTop: bdr,
                    borderBottom: bdr,
                    borderLeft: "none",
                    borderRight: bdr,
                    textAlign: "center",
                    fontSize: 14,
                    fontWeight: 700,
                    color: C.savingsPos,
                    fontFamily: FONT,
                    whiteSpace: "nowrap",
                  }}
                >
                  Saving Total
                </Box>
              </Box>
            </Box>

            {/* tbody */}
            <Box component="tbody">
              {tableRows.map((row, ri) => {
                if (!isRowVisible(row, collSec, collCat)) return null;

                // info-row
                if (row.kind === "info-row") {
                  const isFirst = ri === 0;
                  return (
                    <Box component="tr" key={ri}>
                      <Box
                        component="td"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.categoryBg,
                          borderTop: isFirst ? bdr : undefined,
                          borderBottom: bdr,
                          height: 28,
                          pl: "14px",
                          pr: "10px",
                          fontSize: 12,
                          fontWeight: 700,
                          color: C.cellText,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          borderRight: GROUP_DIVIDER_R,
                        }}
                      >
                        {row.label}
                      </Box>
                      {sortedData.map((bd) => (
                        <Box
                          component="td"
                          key={`${bd.budgetId}-${bd.version}`}
                          colSpan={colCountForNegotiationBudget(bd)}
                          sx={{
                            bgcolor: C.categoryBg,
                            borderTop: isFirst ? bdr : undefined,
                            borderBottom: bdr,
                            borderRight: GROUP_DIVIDER_R,
                            height: 28,
                            pl: "14px",
                            fontSize: 12,
                            color: C.cellText,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {getInfoValue(bd, row.fieldKey)}
                        </Box>
                      ))}
                      {/* Saving Total — empty for info rows */}
                      <Box
                        component="td"
                        sx={{
                          bgcolor: "rgba(46,125,50,0.06)",
                          borderTop: isFirst ? bdr : undefined,
                          borderBottom: bdr,
                          borderRight: bdr,
                          height: 28,
                        }}
                      />
                    </Box>
                  );
                }

                // gap
                if (row.kind === "gap") {
                  return (
                    <Box component="tr" key={ri} sx={{ height: row.height }}>
                      <Box
                        component="td"
                        colSpan={totalCols}
                        sx={{ bgcolor: C.white }}
                      />
                    </Box>
                  );
                  // Note: totalCols already includes the Saving Total column
                }

                // section-header
                if (row.kind === "section-header") {
                  const expanded = !collSec.has(row.sectionKey);
                  const sectionStageDivider =
                    firstSectionHeaderRowIndex >= 0 &&
                    ri > firstSectionHeaderRowIndex
                      ? GROUP_DIVIDER_R
                      : undefined;
                  return (
                    <Box component="tr" key={ri}>
                      <Box
                        component="td"
                        onClick={() => onToggleSec(row.sectionKey)}
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.sectionBg,
                          cursor: "pointer",
                          pl: "10px",
                          pr: "8px",
                          py: "10px",
                          verticalAlign: "middle",
                          borderRight: SECTION_INNER_R,
                          borderTop: sectionStageDivider,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            minWidth: 0,
                          }}
                        >
                          <MdKeyboardArrowDown
                            size={18}
                            color={C.white}
                            style={{
                              flexShrink: 0,
                              transform: expanded ? "none" : "rotate(-90deg)",
                              transition: "transform 0.2s",
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.white,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              fontFamily: FONT,
                            }}
                          >
                            {row.label}
                          </Typography>
                        </Box>
                      </Box>
                      {sortedData.flatMap((bd, bi) => {
                        const vals = getValues(
                          bd,
                          row,
                          sortedData,
                          bi,
                          comparisonTaxFlags
                        );
                        const widths = colWidthsForNegotiationBudget(bd);
                        const n = widths.length;
                        return widths.map((_, ci) => {
                          const isSavingsCol =
                            bd.version !== 0 && ci === IDX_SAVINGS;
                          const isValorTotalCol = ci === IDX_VALOR_TOTAL;
                          const show = isValorTotalCol || isSavingsCol;
                          const isLastInGroup = ci === n - 1;
                          return (
                            <Box
                              component="td"
                              key={`${bi}-${ci}`}
                              sx={{
                                bgcolor: C.sectionBg,
                                textAlign: "right",
                                pr: "10px",
                                pl: "10px",
                                py: "10px",
                                verticalAlign: "middle",
                                fontSize: 13,
                                fontWeight: 700,
                                color: show ? C.white : C.sectionBg,
                                whiteSpace: "nowrap",
                                borderRight: isLastInGroup
                                  ? GROUP_DIVIDER_R
                                  : SECTION_INNER_R,
                                borderTop: sectionStageDivider,
                              }}
                            >
                              {show ? String(vals[ci]) : ""}
                            </Box>
                          );
                        });
                      })}
                      {/* Saving Total — mesma faixa azul; borda só à esquerda via última célula do último bloco */}
                      {(() => {
                        const savingTotal = sumSavingsForSavingTotal(
                          sortedData,
                          row,
                          comparisonTaxFlags
                        );
                        return (
                          <Box
                            component="td"
                            sx={{
                              bgcolor: C.sectionBg,
                              textAlign: "right",
                              pr: "10px",
                              pl: "10px",
                              py: "10px",
                              verticalAlign: "middle",
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.white,
                              whiteSpace: "nowrap",
                              borderRight: bdr,
                              borderTop: sectionStageDivider,
                            }}
                          >
                            {savingTotal !== 0 ? fmtCurrency(savingTotal) : "-"}
                          </Box>
                        );
                      })()}
                    </Box>
                  );
                }

                // category-header
                if (row.kind === "category-header") {
                  const catKey = `${row.sectionKey}||${row.categoryKey}`;
                  const expanded = !collCat.has(catKey);
                  return (
                    <Box
                      component="tr"
                      key={ri}
                      sx={{ height: ROW_H["category-header"] }}
                    >
                      <Box
                        component="td"
                        colSpan={totalCols}
                        onClick={() =>
                          onToggleCat(row.sectionKey, row.categoryKey)
                        }
                        sx={{
                          position: "sticky",
                          left: 0,
                          bgcolor: C.categoryBg,
                          cursor: "pointer",
                          borderTop: bdr,
                          borderBottom: bdr,
                          pl: "12px",
                          pr: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          {expanded ? (
                            <MdKeyboardArrowUp
                              size={16}
                              color={C.cellText}
                              style={{ flexShrink: 0 }}
                            />
                          ) : (
                            <MdKeyboardArrowDown
                              size={16}
                              color={C.cellText}
                              style={{ flexShrink: 0 }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.cellText,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontFamily: FONT,
                            }}
                          >
                            {row.label}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                }

                // column-header
                if (row.kind === "column-header") {
                  return (
                    <Box
                      component="tr"
                      key={ri}
                      sx={{ height: ROW_H["column-header"] }}
                    >
                      <Box
                        component="td"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.categoryBg,
                          borderBottom: bdr,
                          borderRight: GROUP_DIVIDER_R,
                        }}
                      />
                      {sortedData.flatMap((bd, bi) => {
                        const labels =
                          bd.version === 0
                            ? [
                                COL_LABELS[IDX_DESCRICAO_VAL],
                                ...COL_LABELS.slice(1, 5),
                                COL_LABELS[IDX_OBS_VAL],
                              ]
                            : COL_LABELS;
                        const n = labels.length;
                        return labels.map((col, ci) => {
                          const isSavings =
                            bd.version !== 0 && ci === IDX_SAVINGS;
                          const isTextCol = ci === 0 || ci === n - 1;
                          const isLastInGroup = ci === n - 1;
                          return (
                            <Box
                              component="td"
                              key={`${bi}-${ci}`}
                              sx={{
                                bgcolor: isSavings
                                  ? "rgba(46,125,50,0.06)"
                                  : C.categoryBg,
                                borderBottom: bdr,
                                borderRight: isLastInGroup
                                  ? GROUP_DIVIDER_R
                                  : bdr,
                                textAlign: isTextCol ? "left" : "right",
                                pr: "10px",
                                pl: "10px",
                                fontSize: 12,
                                fontWeight: 700,
                                color: isSavings ? C.savingsPos : C.cellText,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {col}
                            </Box>
                          );
                        });
                      })}
                      {/* Saving Total column label */}
                      <Box
                        component="td"
                        sx={{
                          bgcolor: "#e8f5e9",
                          borderBottom: bdr,
                          borderRight: bdr,
                          textAlign: "right",
                          pr: "10px",
                          pl: "10px",
                          fontSize: 12,
                          fontWeight: 700,
                          color: C.savingsPos,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Saving Total
                      </Box>
                    </Box>
                  );
                }

                // grand-total
                if (row.kind === "grand-total") {
                  return (
                    <Box component="tr" key={ri}>
                      <Box
                        component="td"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.grandTotalBg,
                          pl: "12px",
                          pr: "8px",
                          py: "10px",
                          verticalAlign: "middle",
                          borderRight: SECTION_INNER_R,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: C.white,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            fontFamily: FONT,
                          }}
                        >
                          {row.label}
                        </Typography>
                      </Box>
                      {sortedData.flatMap((bd, bi) => {
                        const vals = getValues(
                          bd,
                          row,
                          sortedData,
                          bi,
                          comparisonTaxFlags
                        );
                        const widths = colWidthsForNegotiationBudget(bd);
                        const n = widths.length;
                        return widths.map((_, ci) => {
                          const isSavingsCol =
                            bd.version !== 0 && ci === IDX_SAVINGS;
                          const isValorTotalCol = ci === IDX_VALOR_TOTAL;
                          const show = isValorTotalCol || isSavingsCol;
                          const isLastInGroup = ci === n - 1;
                          return (
                            <Box
                              component="td"
                              key={`${bi}-${ci}`}
                              sx={{
                                bgcolor: C.grandTotalBg,
                                textAlign: "right",
                                pr: "10px",
                                pl: "10px",
                                py: "10px",
                                verticalAlign: "middle",
                                fontSize: 13,
                                fontWeight: 700,
                                color: show ? C.white : C.grandTotalBg,
                                whiteSpace: "nowrap",
                                borderRight: isLastInGroup
                                  ? GROUP_DIVIDER_R
                                  : SECTION_INNER_R,
                              }}
                            >
                              {show ? String(vals[ci]) : ""}
                            </Box>
                          );
                        });
                      })}
                      {/* Saving Total for grand-total */}
                      {(() => {
                        const savingTotal = sumSavingsForSavingTotal(
                          sortedData,
                          row,
                          comparisonTaxFlags
                        );
                        return (
                          <Box
                            component="td"
                            sx={{
                              bgcolor: C.grandTotalBg,
                              textAlign: "right",
                              pr: "10px",
                              pl: "10px",
                              py: "10px",
                              verticalAlign: "middle",
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.white,
                              whiteSpace: "nowrap",
                              borderRight: bdr,
                            }}
                          >
                            {savingTotal !== 0 ? fmtCurrency(savingTotal) : "-"}
                          </Box>
                        );
                      })()}
                    </Box>
                  );
                }

                // separator
                if (row.kind === "separator") {
                  return (
                    <Box component="tr" key={ri} sx={{ height: 4 }}>
                      <Box
                        component="td"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.separatorBg,
                        }}
                      />
                      {sortedData.flatMap((bd, bi) => {
                        const sepWidths = colWidthsForNegotiationBudget(bd);
                        const sn = sepWidths.length;
                        return sepWidths.map((__, ci) => (
                          <Box
                            component="td"
                            key={`${bi}-${ci}`}
                            sx={{
                              bgcolor: C.separatorBg,
                              borderRight:
                                ci === sn - 1 ? GROUP_DIVIDER_R : "none",
                            }}
                          />
                        ));
                      })}
                      <Box component="td" sx={{ bgcolor: C.separatorBg }} />
                    </Box>
                  );
                }

                // service / category-total / section-total
                const isSvcRow = row.kind === "service";
                const isCatTotal = row.kind === "category-total";
                const isSecTotal = row.kind === "section-total";
                const isLastCatTotal = isCatTotal && (row as any).isLast;
                const rowBg = isSecTotal ? C.categoryBg : C.white;

                const labelSx: any = {
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  bgcolor: rowBg,
                  height: ROW_H[row.kind],
                  pl: "14px",
                  pr: "10px",
                  fontSize: 13,
                  fontFamily: FONT,
                  color: C.cellText,
                  fontWeight: isSecTotal ? 700 : 400,
                  borderBottom: isLastCatTotal ? "none" : bdr,
                  borderTop: isSecTotal ? bdr : "none",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  borderRight: GROUP_DIVIDER_R,
                };

                const savingTotal = sumSavingsForSavingTotal(
                  sortedData,
                  row,
                  comparisonTaxFlags
                );
                const savingTotalStr =
                  savingTotal !== 0 ? fmtCurrency(savingTotal) : "-";
                const hasSavingTotal = savingTotal !== 0;
                // positive total = more expensive overall → red; negative = cheaper → green
                const savingTotalColor =
                  savingTotal > 0 ? C.savingsNeg : C.savingsPos;
                const savingTotalBg =
                  savingTotal > 0
                    ? "rgba(211,47,47,0.05)"
                    : "rgba(46,125,50,0.05)";

                return (
                  <Box component="tr" key={ri} sx={{ height: ROW_H[row.kind] }}>
                    <Box component="td" sx={labelSx}>
                      {(row as any).label}
                    </Box>
                    {(() => {
                      const baseBd = sortedData.find((d) => d.version === 0);
                      const baseTotal = baseBd
                        ? getTotalRaw(baseBd, row, comparisonTaxFlags)
                        : 0;
                      return sortedData.flatMap((bd, bi) => {
                        const vals = getValues(
                          bd,
                          row,
                          sortedData,
                          bi,
                          comparisonTaxFlags
                        );
                        const rawSavings = getSavingsRaw(
                          sortedData,
                          bi,
                          row,
                          comparisonTaxFlags
                        );
                        const rawTotal = getTotalRaw(
                          bd,
                          row,
                          comparisonTaxFlags
                        );
                        const widths = colWidthsForNegotiationBudget(bd);
                        const n = widths.length;
                        return widths.map((_, ci) => {
                          const isSavingsCol =
                            bd.version !== 0 && ci === IDX_SAVINGS;
                          const isValorTotalCol = ci === IDX_VALOR_TOTAL;
                          const isObsCol = ci === n - 1;
                          // Descrição sempre fica em ci=0=vals[0] nos dois layouts (base e
                          // negociado). Só o Obs (última coluna) "desliza" no orçamento base
                          // (sem Savings) e precisa remapear pro índice fixo do array de
                          // valores em vez de usar `ci` direto.
                          const isTextCol = ci === 0 || isObsCol;
                          const cellVal = isObsCol
                            ? vals[IDX_OBS_VAL]
                            : vals[ci];
                          let cellStyle: { color: string; bgcolor: string };
                          if (isSavingsCol) {
                            cellStyle = savingsStyle(rawSavings);
                          } else if (isValorTotalCol && bd.version !== 0) {
                            cellStyle = totalStyle(rawTotal, baseTotal);
                          } else {
                            cellStyle = { color: C.cellText, bgcolor: rowBg };
                          }
                          const hasHighlight =
                            cellStyle.bgcolor !== "transparent" &&
                            cellStyle.bgcolor !== rowBg;
                          const isLastInGroup = ci === n - 1;
                          const cellSx: any = {
                            bgcolor:
                              isSavingsCol ||
                              (isValorTotalCol && bd.version !== 0)
                                ? cellStyle.bgcolor
                                : rowBg,
                            textAlign: isTextCol ? "left" : "right",
                            pr: "10px",
                            pl: "10px",
                            fontSize: 13,
                            fontFamily: FONT,
                            color: cellStyle.color,
                            fontWeight: isSecTotal || hasHighlight ? 700 : 400,
                            borderRight: isLastInGroup ? GROUP_DIVIDER_R : bdr,
                            borderBottom: isLastCatTotal ? "none" : bdr,
                            borderTop: isSecTotal ? bdr : "none",
                            whiteSpace: "nowrap",
                          };
                          if (isTextCol) {
                            return (
                              <ObsCell
                                key={`${bi}-${ci}`}
                                value={String(cellVal)}
                                baseSx={cellSx}
                              />
                            );
                          }
                          return (
                            <Box component="td" key={`${bi}-${ci}`} sx={cellSx}>
                              {String(cellVal)}
                            </Box>
                          );
                        });
                      });
                    })()}
                    {/* Saving Total cell — green if sum negative (cheaper), red if positive (more expensive) */}
                    <Box
                      component="td"
                      sx={{
                        bgcolor: savingTotalBg,
                        textAlign: "right",
                        pr: "10px",
                        fontSize: 13,
                        fontFamily: FONT,
                        color: savingTotalColor,
                        fontWeight: isSecTotal || hasSavingTotal ? 700 : 400,
                        borderBottom: isLastCatTotal ? "none" : bdr,
                        borderTop: isSecTotal ? bdr : "none",
                        borderRight: bdr,
                        pl: "10px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {savingTotalStr}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NegotiationComparisonModal;
